/**
 * Composer awareness for every screen: what the working draft holds, send and remove from wherever the
 * user is, and the little badge that says a snippet is already in the cut.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Icon } from './icons';
import type { RowMenuItem } from './row-menu';
import { queryClient, trpc } from '@/lib/trpc';

export function useComposerActions() {
  const working = useQuery(trpc.compose.working.queryOptions(undefined, { refetchInterval: 20_000 }));
  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.compose.working.queryKey() });
  const add = useMutation(trpc.compose.addSlot.mutationOptions({ onSuccess: refresh }));
  const remove = useMutation(trpc.compose.removeSnippet.mutationOptions({ onSuccess: refresh }));
  const inDraft = (snippetId: string) => working.data?.snippetIds.includes(snippetId) ?? false;
  return {
    inDraft,
    sendSnippet: (snippetId: string) => add.mutate({ snippetId }),
    sendMany: (snippetIds: string[]) => add.mutate({ snippetIds }),
    sendAsset: (assetId: string) => add.mutate({ assetId }),
    removeSnippet: (snippetId: string) => remove.mutate({ snippetId }),
    /** The standard menu entries: a tick when it is already in the cut, remove beneath. */
    snippetMenuItems: (snippetId: string): RowMenuItem[] => inDraft(snippetId)
      ? [
        { label: '✓ In the Composer', onClick: () => undefined, disabled: true },
        { label: 'Remove from Composer', onClick: () => remove.mutate({ snippetId }) },
      ]
      : [{ label: 'Send to Composer', onClick: () => add.mutate({ snippetId }) }],
    busy: add.isPending || remove.isPending,
  };
}

/** Small marker on anything already sent, so nothing gets sent twice by accident. */
export function SentBadge() {
  return (
    <span className="sent-badge" title="Already sent to the Composer">
      <Icon name="film" />
    </span>
  );
}
